import Api from '@/modules/core/api/api.ts';
import axios from 'axios';
import {
  DOC_FILES_GET,
  DOC_SIGNATURES_FILES_GET,
  SYSTEM_FILE_GET,
  SYSTEM_FILE_SIGN_MULTIPLE,
} from '@/modules/core/configs/end-points.ts';
import { FileApi } from '@/modules/shared/feature-bridges.ts';
import { crc32 } from 'js-crc';
import { encode, toUint8Array } from 'js-base64';
import { defineStore } from 'pinia';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import { Direction } from '@/modules/common/enums/direction.enum.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

export type SignFile = {
  F_ID: number;
  DOC_ID: number;
  LOGIN_ID: number;
  PARENT_ID: number;
  PARENT_TABLE_ID: number;
  Номер: number;
  Название: string;
  Размер: number;
  Файл: string;
  Редакция: number;
  Создан: string;
  Пользователь: string;
  ЭП: number;
  Индикатор: number | boolean;
  Примечание?: string | null;
  sign: unknown;
  active: boolean;
  base64: string | null;
};

type SignState = {
  filesForSign: SignFile[];
  filesForSignAbortController: AbortController | null;
};

const SIGN_POLL_FILTER_MAX_IDS = 250;

function chunkFileIds(fileIds: number[]) {
  return Array.from({ length: Math.ceil(fileIds.length / SIGN_POLL_FILTER_MAX_IDS) }, (_, index) =>
    fileIds.slice(index * SIGN_POLL_FILTER_MAX_IDS, (index + 1) * SIGN_POLL_FILTER_MAX_IDS)
  );
}

export const useSignStore = defineStore(StoreId.Sign, {
  state: (): SignState => ({
    filesForSign: [],
    filesForSignAbortController: null,
  }),
  getters: {
    filesForSignHasRemarkGetter(state): boolean {
      return state.filesForSign.some((file) => !!file['Примечание']);
    },
    filesForSignLengthGetter(state): number {
      return state.filesForSign.length;
    },
    filesForSignAllLoadGetter(state): boolean {
      return state.filesForSign.length === state.filesForSign.filter((file) => file?.base64).length;
    },
    filesForSignConfigAxiosGetter(state): AbortSignal {
      // TODO: Side effect
      state.filesForSignAbortController = new AbortController();
      return state.filesForSignAbortController.signal;
    },
    filesForSignSelectedGetter(state): SignFile[] {
      return state.filesForSign.filter((file) => file.active);
    },
    hasFilesForSignSelectedGetter(state): boolean {
      return state.filesForSign.filter((file) => file.active).length > 0;
    },
  },
  actions: {
    async getFilesForSignAction(documentId: number) {
      return await GatewayApi.execute<ItemsDto<SignFile>>(DOC_FILES_GET, {
        params: { doc_id: documentId },
        filter: GatewayFilter.and(GatewayFilter.isTrue('Индикатор')),
        orderBy: [{ field: 'Создан', direction: Direction.Desc }],
        signal: this.filesForSignConfigAxiosGetter,
      }).then((response: { items: SignFile[] }) => {
        const length = response.items.length;

        for (let i = 0; i < length; i++) {
          const file = response.items[i];
          file.sign = null;
          file.active = false;
          file.base64 = null;

          this.filesForSign.push(file);
          this.getFileForSignCreateHashAction(file);
        }
      });
    },
    async applyFilesForSignAction(documentId: number) {
      const files = this.filesForSignSelectedGetter;

      if (!files.length) {
        return;
      }

      const items: { f_id: number; sign_content: string | null; sign_type: string }[] = [];
      files.forEach((file) => {
        items.push({
          f_id: file.F_ID,
          sign_content: file.base64,
          sign_type: 'CRC32',
        });
      });

      const response = await Api.post(SYSTEM_FILE_SIGN_MULTIPLE, { items }, this.filesForSignConfigAxiosGetter);

      const fileIds: number[] = response.file_ids;
      const fileIdChunks = chunkFileIds(fileIds);

      const limit = 40;

      let offset = 40;

      let i = 0;

      let lastCount = 0;

      return new Promise<void>((resolve, reject) => {
        const interval = setInterval(() => {
          if (offset < i) {
            clearInterval(interval);

            reject('Ошибка подписания документов');
            return;
          }

          i++;
          const signal = this.filesForSignConfigAxiosGetter;

          Promise.all(
            fileIdChunks.map((chunk) =>
              GatewayApi.execute<ItemsDto<unknown>>(DOC_SIGNATURES_FILES_GET, {
                params: { doc_id: documentId },
                filter: GatewayFilter.and(GatewayFilter.in('F_ID', chunk)),
                signal,
              })
            )
          ).then((responses) => {
            const count = responses.reduce((total, response) => total + response.count, 0);

            if (lastCount !== count) {
              lastCount = count;
              offset += limit;
            }
            if (count === fileIds.length) {
              clearInterval(interval);
              resolve();
            }
          });
        }, 1000);
      });
    },
    async getFileForSignCreateHashAction(file: SignFile) {
      const limit = 30;

      let i = 0;
      let addPreview = false;

      const getFile = () => {
        Api.post(
          SYSTEM_FILE_GET,
          {
            file_id: file.F_ID,
          },
          this.filesForSignConfigAxiosGetter
        )
          .then((response: { file_base64: string }) => {
            const data = toUint8Array(response.file_base64);
            const crc32code = crc32(data);
            const base64 = encode(crc32code);

            this.filesForSignSetHashMutations({ file_id: file['F_ID'], base64 });
          })
          .catch((e: unknown) => {
            i++;
            if (!addPreview && !axios.isCancel(e)) {
              FileApi.addView(file['F_ID']);
              addPreview = true;
            }

            if (limit < i) {
              return;
            }

            setTimeout(getFile, 2000);
          });
      };

      getFile();
    },
    clear() {
      if (this.filesForSignAbortController) {
        this.filesForSignAbortController.abort();
      }

      this.$reset();
    },
    filesForSignSetHashMutations({ file_id, base64 }: { file_id: number; base64: string }) {
      this.filesForSign = this.filesForSign.map((file) => (file['F_ID'] === file_id ? { ...file, base64 } : file));
    },
    filesForSignSetFileActive({ file_id, active }: { file_id: number; active: boolean }) {
      const file = this.filesForSign.find((item) => item['F_ID'] === file_id);
      if (file) {
        file.active = active;
      }
    },
    filesForSinSetActive(bool: boolean) {
      this.filesForSign = this.filesForSign.map((file) => (file.base64 ? { ...file, active: bool } : file));
    },
  },
});
